import { useEffect } from "react";

/**
 * iOS-Safari-safe body scroll lock.
 *
 * `document.body.style.overflow = "hidden"` is enough on desktop but iOS Safari
 * keeps rubber-band scrolling the page underneath. The robust pattern is to
 * fix the body in place at its current scroll position, then restore on unlock.
 *
 * We also reference-count the lock so multiple drawers/modals nested or stacked
 * don't accidentally unlock each other.
 */

let lockCount = 0;
let savedScrollY = 0;
let savedBodyStyle: {
  position: string;
  top: string;
  width: string;
  overflow: string;
} | null = null;

export const lockBodyScroll = (): void => {
  if (typeof document === "undefined") return;
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    savedBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
};

export const unlockBodyScroll = (): void => {
  if (typeof document === "undefined") return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && savedBodyStyle) {
    document.body.style.position = savedBodyStyle.position;
    document.body.style.top = savedBodyStyle.top;
    document.body.style.width = savedBodyStyle.width;
    document.body.style.overflow = savedBodyStyle.overflow;
    savedBodyStyle = null;
    window.scrollTo(0, savedScrollY);
  }
};

/** Hook that locks the body scroll while `active` is true and restores on unmount/false. */
export const useScrollLock = (active: boolean): void => {
  useEffect(() => {
    if (!active) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [active]);
};
